import React, { useState, useEffect } from 'react';
import { Copy, Check, RefreshCw } from 'lucide-react';

interface IntegrationCodeDisplayProps {
  code: string;
  expiresAt: string;
  onGenerateNew: () => void;
  isGenerating: boolean;
}

const IntegrationCodeDisplay: React.FC<IntegrationCodeDisplayProps> = ({
  code,
  expiresAt,
  onGenerateNew,
  isGenerating
}) => {
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const updateTimeLeft = () => {
      const now = new Date();
      const expires = new Date(expiresAt);
      const diff = expires.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft('Истек');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeLeft(`${hours}ч ${minutes}м`);
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 60000); // Обновляем каждую минуту

    return () => clearInterval(interval);
  }, [expiresAt]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Код подключения
      </h3>

      <div className="bg-gray-50 rounded-lg p-6 mb-4">
        <div className="text-center">
          <div className="text-4xl font-mono font-bold text-blue-600 mb-2 tracking-wider">
            {code}
          </div>
          <div className="text-sm text-gray-500">
            Действителен еще: {timeLeft}
          </div>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="text-sm text-gray-700">
          <p className="font-medium mb-2">Инструкция для клиента:</p>
          <ol className="list-decimal list-inside space-y-1 text-gray-600">
            <li>Откройте бот в Telegram: <a href="https://t.me/Meal_Monitor_Bot" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">@Meal_Monitor_Bot</a></li>
            <li>Выберите "🏋️ Подключить тренера" из меню бота</li>
            <li>Нажмите кнопку <span className="font-semibold">"🔗 Подключить тренера"</span></li>
            <li>Введите код подключения: <span className="font-mono font-bold">{code}</span></li>
          </ol>
        </div>

      </div>

      <div className="flex gap-2">
        <button
          onClick={handleCopy}
          className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {copied ? (
            <>
              <Check className="mr-2 h-4 w-4 text-green-500" />
              Скопировано
            </>
          ) : (
            <>
              <Copy className="mr-2 h-4 w-4" />
              Скопировать код
            </>
          )}
        </button>

        <button
          onClick={onGenerateNew}
          disabled={isGenerating}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} />
          Новый код
        </button>
      </div>
    </div>
  );
};

export default IntegrationCodeDisplay;
